import { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import KpiCard from '../components/KpiCard';
import { IconWallet, IconTrendingUp, IconPercent, IconReceipt } from '../components/icons';
import FilterBar from '../components/FilterBar';
import ColumnToggle from '../components/ColumnToggle';
import {
  getSummary,
  getByCustomer,
  getBySalesman,
  getByProduct,
  getByChannel,
  getByMonth,
  getTrend,
  getFilterOptions,
} from '../api/dashboard';
import { listSalesGrouped, exportSalesGroupedCsv } from '../api/sales';
import { formatCurrency, formatPercent, formatQty } from '../utils/format';
import { getDefaultDashboardFilters, yearDateRange, detectSelectedYear } from '../utils/filters';
import { useAuth } from '../context/AuthContext';

// Validated categorical palette (fixed order — never cycled per-series, only for
// identity slices like Channel where each entry is its own category).
const COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const REVENUE_COLOR = '#2a78d6'; // categorical slot 1 (blue)
const GP_COLOR = '#1baf7a'; // categorical slot 3 (aqua) — validated pair with blue
const GRID_COLOR = '#e1e0d9';
const AXIS_COLOR = '#898781';
const TOOLTIP_STYLE = {
  contentStyle: { borderRadius: 10, border: '1px solid #e1e0d9', boxShadow: '0 4px 16px rgba(11,11,11,0.08)', fontSize: 12 },
  labelStyle: { color: '#52514e', fontWeight: 600, marginBottom: 4 },
};

// Transaction Detail's column set, in canonical order. Channel, Sub-Channel and Category
// start hidden — they're useful but secondary, so they don't crowd the default view.
const TRANSACTION_DETAIL_COLUMNS = [
  { key: 'lastDate', label: 'Last Date', nowrap: true },
  { key: 'customerName', label: 'Customer' },
  { key: 'channel', label: 'Channel', nowrap: true, defaultHidden: true },
  { key: 'subChannel', label: 'Sub-Channel', nowrap: true, defaultHidden: true },
  { key: 'salesmanName', label: 'Salesman', nowrap: true },
  { key: 'productCode', label: 'Part Number', nowrap: true },
  { key: 'productName', label: 'Description' },
  { key: 'category', label: 'Category', nowrap: true, defaultHidden: true },
  { key: 'unit', label: 'Unit', nowrap: true },
  { key: 'transactionCount', label: 'Invoices', align: 'right' },
  { key: 'qty', label: 'Total Qty', align: 'right' },
  { key: 'revenue', label: 'Revenue', align: 'right' },
  { key: 'cost', label: 'Cost', align: 'right', gpOnly: true },
  { key: 'gpAmount', label: 'GP', align: 'right', gpOnly: true },
  { key: 'gpPercent', label: 'GP %', align: 'right', gpOnly: true },
];
const TRANSACTION_DETAIL_COLUMNS_STORAGE_KEY = 'gp.transactionDetail.columns.v1';

function loadColumnVisibility() {
  const defaults = Object.fromEntries(TRANSACTION_DETAIL_COLUMNS.map((c) => [c.key, !c.defaultHidden]));
  try {
    const saved = JSON.parse(localStorage.getItem(TRANSACTION_DETAIL_COLUMNS_STORAGE_KEY) || '{}');
    return { ...defaults, ...saved };
  } catch {
    return defaults;
  }
}

function renderTransactionCell(key, t) {
  switch (key) {
    case 'lastDate':
      return new Date(t.lastDate).toLocaleDateString();
    case 'unit':
      return (
        <span
          className={
            t.multiUnit
              ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-medium'
              : ''
          }
        >
          {t.unit}
        </span>
      );
    case 'revenue':
    case 'cost':
      return formatCurrency(t[key]);
    case 'gpAmount':
      return <span className="text-green-600">{formatCurrency(t.gpAmount)}</span>;
    case 'gpPercent':
      return formatPercent(t.gpPercent);
    default:
      return t[key];
  }
}

export default function Dashboard() {
  const { canViewGP } = useAuth();
  const [filters, setFilters] = useState(getDefaultDashboardFilters);
  const [options, setOptions] = useState({ customers: [], salesmen: [], products: [], categories: [], channels: [], subChannels: [] });
  const [summary, setSummary] = useState(null);
  const [byCustomer, setByCustomer] = useState([]);
  const [bySalesman, setBySalesman] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [byChannel, setByChannel] = useState([]);
  const [byMonth, setByMonth] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedYear = detectSelectedYear(filters);

  useEffect(() => {
    // Salesmen are scoped server-side to the selected Year, so a salesman with zero
    // sales that year never shows up as a filter option in the first place.
    getFilterOptions(selectedYear ? { year: selectedYear } : undefined).then((opts) => {
      setOptions(opts);
      setFilters((f) => {
        let next = f;
        // The default view opens on the current calendar year, but if that year has no
        // data at all (e.g. it's a new year with nothing uploaded yet, or stale data for
        // it was removed), fall back to the most recent year that actually has data
        // instead of silently showing an empty dashboard.
        const year = detectSelectedYear(next);
        if (year && opts.years?.length && !opts.years.includes(Number(year))) {
          next = { ...next, ...yearDateRange(opts.years[0]) };
        }
        if (next.salesmanCode && !opts.salesmen.some((s) => s.code === next.salesmanCode)) {
          next = { ...next, salesmanCode: '' };
        }
        return next;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    // Month-over-month views (Monthly Comparison, Revenue & GP Trend) exist to compare
    // across months, so the From/To date range — which defaults to just one month —
    // shouldn't constrain them. Other filters (customer/salesman/product/channel) still
    // apply so drill-downs keep working.
    const { from, to, ...paramsAllMonths } = params;
    setLoading(true);
    Promise.all([
      getSummary(params),
      getByCustomer(params),
      getBySalesman(params),
      getByProduct(params),
      getByChannel(params),
      getByMonth(paramsAllMonths),
      getTrend(paramsAllMonths),
    ]).then(([s, c, sm, p, ch, mo, t]) => {
      setSummary(s);
      setByCustomer(c.slice(0, 10));
      setBySalesman(sm.slice(0, 10));
      setByProduct(p.slice(0, 10));
      setByChannel(ch);
      setByMonth(mo);
      setTrend(t);
      setLoading(false);
    });
  }, [filters]);

  function drillTo(key, value) {
    if (value === undefined || value === null || value === '') return;
    setFilters((f) => ({ ...f, [key]: value }));
  }

  function drillToMonth(period) {
    if (!period) return;
    const [y, m] = period.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    setFilters((f) => ({ ...f, from: `${period}-01`, to: `${period}-${String(lastDay).padStart(2, '0')}` }));
  }

  const filterLabels = {
    customerCode: options.customers.find((c) => c.code === filters.customerCode)?.name,
    salesmanCode: options.salesmen.find((s) => s.code === filters.salesmanCode)?.name,
    productCode: options.products.find((p) => p.code === filters.productCode)?.name,
    category: filters.category,
    channel: filters.channel,
    subChannel: filters.subChannel,
    from: filters.from,
    to: filters.to,
  };
  const chipDefs = [
    ['from', 'From'],
    ['to', 'To'],
    ['customerCode', 'Customer'],
    ['salesmanCode', 'Salesman'],
    ['productCode', 'Product'],
    ['category', 'Category'],
    ['channel', 'Channel'],
    ['subChannel', 'Sub-Channel'],
  ];
  const activeChips = chipDefs.filter(([key]) => filters[key]);

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-4">Sales & GP Dashboard</h1>

      <FilterBar filters={filters} onChange={setFilters} options={options} />

      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 -mt-4 mb-6">
          <span className="text-xs text-gray-500">Drilled into:</span>
          {activeChips.map(([key, label]) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1"
            >
              {label}: {filterLabels[key] ?? filters[key]}
              <button
                onClick={() => setFilters((f) => ({ ...f, [key]: '' }))}
                className="text-blue-500 hover:text-blue-800"
                aria-label={`Remove ${label} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button onClick={() => setFilters(getDefaultDashboardFilters())} className="text-xs text-blue-600 hover:underline">
            Clear all
          </button>
        </div>
      )}

      {loading || !summary ? (
        <div className="text-gray-400 text-sm">Loading dashboard…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <KpiCard label="Total Value" value={formatCurrency(summary.revenue)} accent="text-blue-600" tone="blue" icon={<IconWallet />} />
            <KpiCard label="Total Quantity" value={formatQty(summary.qty)} accent="text-violet-600" tone="violet" icon={<IconReceipt />} />
          </div>

          {canViewGP && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 md:grid-cols-3">
              <KpiCard label="Total Cost" value={formatCurrency(summary.cost)} tone="gray" icon={<IconWallet />} />
              <KpiCard
                label="Gross Profit"
                value={formatCurrency(summary.gpAmount)}
                accent="text-emerald-600"
                tone="green"
                icon={<IconTrendingUp />}
              />
              <KpiCard
                label="GP %"
                value={formatPercent(summary.gpPercent)}
                accent="text-blue-600"
                tone="blue"
                icon={<IconPercent />}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChartCard title="Revenue & GP by Customer (Top 10)" hint="Click a bar to drill in">
              <BarChart data={byCustomer}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatCurrency(v)} cursor={{ fill: 'rgba(42,120,214,0.06)' }} {...TOOLTIP_STYLE} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                <Bar
                  dataKey="revenue"
                  fill={REVENUE_COLOR}
                  radius={[4, 4, 0, 0]}
                  name="Revenue"
                  cursor="pointer"
                  onClick={(d) => drillTo('customerCode', d.code)}
                />
                {canViewGP && (
                  <Bar
                    dataKey="gpAmount"
                    fill={GP_COLOR}
                    radius={[4, 4, 0, 0]}
                    name="GP"
                    cursor="pointer"
                    onClick={(d) => drillTo('customerCode', d.code)}
                  />
                )}
              </BarChart>
            </ChartCard>

            <ChartCard title="Revenue & GP by Salesman (Top 10)" hint="Click a bar to drill in">
              <BarChart data={bySalesman}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatCurrency(v)} cursor={{ fill: 'rgba(42,120,214,0.06)' }} {...TOOLTIP_STYLE} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                <Bar
                  dataKey="revenue"
                  fill={REVENUE_COLOR}
                  radius={[4, 4, 0, 0]}
                  name="Revenue"
                  cursor="pointer"
                  onClick={(d) => drillTo('salesmanCode', d.code)}
                />
                {canViewGP && (
                  <Bar
                    dataKey="gpAmount"
                    fill={GP_COLOR}
                    radius={[4, 4, 0, 0]}
                    name="GP"
                    cursor="pointer"
                    onClick={(d) => drillTo('salesmanCode', d.code)}
                  />
                )}
              </BarChart>
            </ChartCard>

            <ChartCard title="Revenue & GP by Product (Top 10)" hint="Click a bar to drill in">
              <BarChart data={byProduct}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                <XAxis dataKey="code" tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => formatCurrency(v)} cursor={{ fill: 'rgba(42,120,214,0.06)' }} {...TOOLTIP_STYLE} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
                <Bar
                  dataKey="revenue"
                  fill={REVENUE_COLOR}
                  radius={[4, 4, 0, 0]}
                  name="Revenue"
                  cursor="pointer"
                  onClick={(d) => drillTo('productCode', d.code)}
                />
                {canViewGP && (
                  <Bar
                    dataKey="gpAmount"
                    fill={GP_COLOR}
                    radius={[4, 4, 0, 0]}
                    name="GP"
                    cursor="pointer"
                    onClick={(d) => drillTo('productCode', d.code)}
                  />
                )}
              </BarChart>
            </ChartCard>

            <ChartCard title="Revenue by Channel" hint="Click a slice to drill in">
              <PieChart>
                <Pie
                  data={byChannel}
                  dataKey="revenue"
                  nameKey="code"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  cornerRadius={4}
                  label={{ fontSize: 11, fill: '#52514e' }}
                  cursor="pointer"
                  onClick={(d) => drillTo('channel', d.code)}
                >
                  {byChannel.map((entry, i) => (
                    <Cell key={entry.code} fill={COLORS[i % COLORS.length]} stroke="#fcfcfb" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} cursor={{ fill: 'rgba(42,120,214,0.06)' }} {...TOOLTIP_STYLE} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
              </PieChart>
            </ChartCard>
          </div>

          <ChartCard title="Revenue & GP Trend" hint="Click a point to drill into that month">
            <LineChart
              data={trend}
              onClick={(e) => e?.activeLabel && drillToMonth(e.activeLabel)}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: AXIS_COLOR }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => formatCurrency(v)} {...TOOLTIP_STYLE} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: '#52514e' }} />
              <Line type="monotone" dataKey="revenue" stroke={REVENUE_COLOR} strokeWidth={2.5} dot={{ r: 3, fill: REVENUE_COLOR, strokeWidth: 0 }} activeDot={{ r: 5 }} name="Revenue" />
              {canViewGP && (
                <Line type="monotone" dataKey="gpAmount" stroke={GP_COLOR} strokeWidth={2.5} dot={{ r: 3, fill: GP_COLOR, strokeWidth: 0 }} activeDot={{ r: 5 }} name="GP" />
              )}
            </LineChart>
          </ChartCard>

          <MonthlyComparisonTable rows={byMonth} canViewGP={canViewGP} onDrill={drillToMonth} />

          <TransactionDetailTable filters={filters} />
        </>
      )}
    </div>
  );
}

function MonthChangeCell({ value }) {
  if (value === null || value === undefined) return <td className="px-4 py-2 text-right text-gray-400">—</td>;
  const positive = value >= 0;
  return (
    <td className={`px-4 py-2 text-right ${positive ? 'text-green-600' : 'text-red-600'}`}>
      {positive ? '▲' : '▼'} {formatPercent(Math.abs(value))}
    </td>
  );
}

function MonthlyComparisonTable({ rows, canViewGP, onDrill }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-6">
      <div className="px-4 py-3 border-b border-gray-100 flex items-baseline justify-between">
        <h2 className="text-sm font-medium text-gray-700">Monthly Comparison</h2>
        <span className="text-xs text-gray-400">Click a row to drill into that month</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Month</th>
              <th className="px-4 py-2 font-medium text-right">Revenue</th>
              <th className="px-4 py-2 font-medium text-right">Revenue vs Prior Month</th>
              {canViewGP && (
                <>
                  <th className="px-4 py-2 font-medium text-right">Cost</th>
                  <th className="px-4 py-2 font-medium text-right">GP</th>
                  <th className="px-4 py-2 font-medium text-right">GP vs Prior Month</th>
                  <th className="px-4 py-2 font-medium text-right">GP %</th>
                </>
              )}
              <th className="px-4 py-2 font-medium text-right">Qty</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={canViewGP ? 8 : 4} className="px-4 py-6 text-center text-gray-400">
                  No monthly data for the selected filters
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.month}
                  onClick={() => onDrill(r.month)}
                  className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-4 py-2 font-medium text-gray-800">{r.month}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(r.revenue)}</td>
                  <MonthChangeCell value={r.revenueChangePercent} />
                  {canViewGP && (
                    <>
                      <td className="px-4 py-2 text-right">{formatCurrency(r.cost)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{formatCurrency(r.gpAmount)}</td>
                      <MonthChangeCell value={r.gpChangePercent} />
                      <td className="px-4 py-2 text-right">{formatPercent(r.gpPercent)}</td>
                    </>
                  )}
                  <td className="px-4 py-2 text-right">{r.qty}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartCard({ title, hint, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      <ResponsiveContainer width="100%" height={280}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

const MAX_PAGE_SIZE = 10000;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 'All'];

function TransactionDetailTable({ filters }) {
  const { canViewGP } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [data, setData] = useState({ items: [], total: 0, totals: { qty: 0 }, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(loadColumnVisibility);
  const limit = pageSize === 'All' ? MAX_PAGE_SIZE : pageSize;

  function updateVisibleColumns(next) {
    setVisibleColumns(next);
    try {
      localStorage.setItem(TRANSACTION_DETAIL_COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable (private browsing, etc.) — visibility just won't persist
    }
  }

  const toggleableColumns = TRANSACTION_DETAIL_COLUMNS.filter((c) => !c.gpOnly || canViewGP);
  const activeColumns = toggleableColumns.filter((c) => visibleColumns[c.key] !== false);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const activeParams = {
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  };

  useEffect(() => {
    setLoading(true);
    listSalesGrouped({ ...activeParams, page, limit }).then((res) => {
      setData(res);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearch, page, limit]);

  async function handleExport() {
    setExporting(true);
    try {
      await exportSalesGroupedCsv({ ...activeParams, columns: activeColumns.map((c) => c.key).join(',') });
    } finally {
      setExporting(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-medium text-gray-700">Transaction Detail</h2>
        <div className="flex items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, part number, description, salesman, invoice…"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-72"
          />
          <button
            onClick={handleExport}
            disabled={exporting || data.total === 0}
            className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <ColumnToggle columns={toggleableColumns} visible={visibleColumns} onChange={updateVisibleColumns} />
          <span className="text-xs text-gray-500 whitespace-nowrap">{data.total} combinations</span>
        </div>
      </div>
      <div className="flex items-center gap-2 px-4 pt-2 pb-1 text-xs text-gray-500">
        <span className="inline-block w-3 h-3 rounded bg-amber-200 border border-amber-300" />
        Highlighted rows: this product is sold to this customer in more than one unit — each unit is kept as its own row, never combined.
      </div>
      <div className="overflow-auto max-h-[480px]">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left sticky top-0 z-10">
            <tr>
              {activeColumns.map((c) => (
                <th key={c.key} className={`px-4 py-2 font-medium ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={activeColumns.length} className="px-4 py-6 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={activeColumns.length} className="px-4 py-6 text-center text-gray-400">
                  No transactions for the selected filters
                </td>
              </tr>
            ) : (
              data.items.map((t) => (
                <tr
                  key={`${t.customerCode}-${t.salesmanCode}-${t.productCode}-${t.unit}`}
                  className={`border-t border-gray-100 ${t.multiUnit ? 'bg-amber-50' : ''}`}
                  title={t.multiUnit ? 'This product is sold to this customer in more than one unit — quantities are kept separate per unit, not combined.' : undefined}
                >
                  {activeColumns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : ''} ${c.nowrap ? 'whitespace-nowrap' : ''}`}
                    >
                      {renderTransactionCell(c.key, t)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
        >
          Previous
        </button>
        <span className="text-gray-500">
          Page {page} of {totalPages}
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
          >
            Next
          </button>
          <select
            value={pageSize}
            onChange={(e) => {
              const v = e.target.value;
              setPageSize(v === 'All' ? 'All' : Number(v));
              setPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt === 'All' ? 'Show All' : `${opt} / page`}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
